import { ref } from 'vue';
import { crossAppActions } from './cross-app-actions.ts';
import type { FacilityGroupID, FacilityID, TenantID } from '../cross-app-shared/types/db';
import type { FacilityBrief } from '../cross-app-shared/types/common';

export const facilitiesList = ref<FacilityBrief[]>([]);
export const facilitiesByFacilityID = ref<Record<FacilityID, FacilityBrief>>({});
export const groupsByGroupID = ref<
  Record<FacilityGroupID, { tenantID: TenantID; facilityGroupID: FacilityGroupID; tenantName: string; name: string }>
>({});
export const tenantsByTenantID = ref<Record<TenantID, { tenantID: TenantID; name: string }>>({});

export async function facilitiesRefresh() {
  facilitiesList.value = (await crossAppActions.facilities.list().catch(() => [])).sort((a, b) => {
    const tenantDiff = a.tenantID - b.tenantID;
    if (tenantDiff !== 0) {
      return tenantDiff;
    }

    const groupNameDiff = a.groupName.localeCompare(b.groupName);
    if (groupNameDiff !== 0) {
      return groupNameDiff;
    }

    return a.name.localeCompare(b.name);
  });

  const newFacilitiesRecord: Record<FacilityID, FacilityBrief> = {};
  const newGroupsRecord: Record<
    FacilityGroupID,
    { tenantID: TenantID; tenantName: string; facilityGroupID: FacilityGroupID; name: string }
  > = {};
  const newTenantsRecord: Record<TenantID, { tenantID: TenantID; name: string }> = {};
  for (const facility of facilitiesList.value) {
    newFacilitiesRecord[facility.facilityID] = facility;
    newGroupsRecord[facility.facilityGroupID] = {
      tenantID: facility.tenantID,
      facilityGroupID: facility.facilityGroupID,
      tenantName: facility.tenantName,
      name: facility.groupName,
    };
    newTenantsRecord[facility.tenantID] = { tenantID: facility.tenantID, name: facility.tenantName };
  }
  facilitiesByFacilityID.value = newFacilitiesRecord;
  groupsByGroupID.value = newGroupsRecord;
  tenantsByTenantID.value = newTenantsRecord;
}

// Normally unpopulated groups won't appear, but this forces us to fetch everything:
export async function groupsFetchAll() {
  const groups = await crossAppActions.facilities.listGroups().catch(() => []);
  for (const group of groups) {
    if (!groupsByGroupID.value[group.facilityGroupID]) {
      groupsByGroupID.value[group.facilityGroupID] = {
        tenantID: group.tenantID,
        tenantName: group.tenantName,
        facilityGroupID: group.facilityGroupID,
        name: group.groupName,
      };
    }
  }
}

export function facilityFindFromRoute(facilityID: number | string | string[] | undefined | null): FacilityBrief | null {
  if (facilityID === undefined || facilityID === null || Array.isArray(facilityID)) {
    return null;
  }

  const idAsInt = typeof facilityID === 'number' ? facilityID : parseInt(facilityID, 10);
  return facilitiesList.value.find((f) => f.facilityID === idAsInt) ?? null;
}

export function facilityGetNavigation(
  currentFacilityID: FacilityID,
  tenantID: TenantID,
): {
  previous: FacilityBrief | null;
  next: FacilityBrief | null;
  currentIndex: number;
  total: number;
} {
  // Filter facilities by tenant and sort them consistently
  const tenantFacilities = facilitiesList.value
    .filter((f) => f.tenantID === tenantID)
    .sort((a, b) => {
      const groupDiff = a.groupName.localeCompare(b.groupName);
      if (groupDiff !== 0) return groupDiff;
      return a.name.localeCompare(b.name);
    });

  const currentIndex = tenantFacilities.findIndex((f) => f.facilityID === currentFacilityID);

  if (currentIndex === -1) {
    return { previous: null, next: null, currentIndex: -1, total: tenantFacilities.length };
  }

  const previous = currentIndex > 0 ? tenantFacilities[currentIndex - 1] : null;
  const next = currentIndex < tenantFacilities.length - 1 ? tenantFacilities[currentIndex + 1] : null;

  return {
    previous,
    next,
    currentIndex,
    total: tenantFacilities.length,
  };
}
